import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import CookieConsentManager from '@/components/cookies/CookieConsentManager';
import AttributionTracker from '@/components/AttributionTracker';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { trackAnalyticsPageView } from '@/lib/analytics';
import { useMarketingScrollRestoration } from './useMarketingScrollRestoration';

const Index = lazy(() => import('@/pages/Index'));
const BlogPost = lazy(() => import('@/pages/blog/BlogPostPage'));
const Companion = lazy(() => import('@/pages/companion/CompanionAppPage'));
const Biomarker = lazy(() => import('@/pages/platform/BiomarkerNutritionPage'));
const Personalised = lazy(() => import('@/pages/platform/PersonalisedNutritionPage'));
const PublicPage = lazy(() => import('./PublicPage'));
const LegalPage = lazy(() => import('./PublicLegalPage'));

function PageReady() {
  const { pathname, hash } = useLocation();
  useMarketingScrollRestoration();
  useEffect(() => {
    document.documentElement.dataset.pageReady = pathname;
    trackAnalyticsPageView();
    return () => { delete document.documentElement.dataset.pageReady; };
  }, [pathname, hash]);
  return null;
}

export default function MarketingApp() {
  return <QueryClientProvider client={queryClient}>
    <Toaster /><Sonner /><CookieConsentManager /><AttributionTracker />
    <Suspense fallback={<div role="status" className="p-8 text-center">Loading…</div>}>
      <Routes>
        <Route path="/" element={<AuthGuard><Index /></AuthGuard>} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/companion-app" element={<Companion />} />
        <Route path="/platform/biomarker-informed-nutrition" element={<Biomarker />} />
        <Route path="/personalised-nutrition-platform" element={<Personalised />} />
        {['/terms', '/platform-terms', '/privacy', '/platform-privacy', '/cookies', '/refund-cancellation-policy', '/founding-circle-agreement', '/data-processing-agreement', '/practitioner-declaration-consent'].map(path => <Route key={path} path={path} element={<LegalPage />} />)}
        <Route path="*" element={<PublicPage />} />
      </Routes>
      <PageReady />
    </Suspense>
  </QueryClientProvider>;
}
